import { createApi } from '@reduxjs/toolkit/dist/query/react';
import {
    AppointmentStatusPayload,
    ICalendarAppointment,
    IGlobalAppointment,
    IAppointment,
    IAppointmentHistory,
    IAppointmentInvitePayload,
    IAppointmentPayload
} from '../models/IAppointment';
import { axiosBaseQuery } from '../utils/axios';
import { TimeSlot } from '../views/scheduling-widget/widget-wizard/types';
import { IService } from '../models/IService';
import { ILocation } from '../models/ILocation';
import { IEmployee } from '../models/IEmployee';
import { IPaginateResponse } from '../models/IPaginateResponse';
import { IPayment } from '../models/IPayment';
import { IAppointmentProduct } from '../models/IProduct';
import {
    ICreateRecurringEventPayload,
    IDetailedRecurringEvent,
    IRecurringEvent,
    IUpdateRecurringEventPayload
} from '../models/IRecurringEvent';

export interface AppointmentsFetchProps {
    mode: string;
    date: string;
    employee?: string;
    location?: string;
    customer?: string;
    customerInfo?: string | null;
}

export interface AppointmentPaginatedFetchProps {
    customerInfo?: string;
    employee?: string;
    location?: string;
    customer?: string;
    page?: number;
    per_page?: number;
}

export interface TimeSlotsParams {
    date: string;
    service: IService['id'];
    location: ILocation['id'];
    employee?: IEmployee['id'];
}

type WorkloadEmployee = {
    employee_id: number;
    working_day_start?: string | null;
    working_day_end?: string | null;
    working_day_duration: number;
    appointments_min_start_at?: string | null;
    appointments_max_end_at: string | null;
    appointments_duration: number;
    appointments_count: number;
};

export type WorkloadEntry = {
    employees: Array<WorkloadEmployee>;
    total_working_day_duration: number;
    total_appointments_duration: number;
};

export type WorkloadArgs = {
    date_from: string;
    date_to: string;
    location_id: number;
    employee_ids: number[];
};

type WorkloadData = {
    days: Record<string, WorkloadEntry>;
    timezone: string;
};

const appointmentAPI = createApi({
    reducerPath: 'appointmentAPI',
    tagTypes: ['Appointment', 'EventEmployees', 'AppointmentProducts', 'RecurringEvents', 'WorkloadData'],
    baseQuery: axiosBaseQuery(),
    endpoints: (build) => ({
        fetchAllAppointments: build.query<ICalendarAppointment[], AppointmentsFetchProps>({
            query: ({ mode, date, employee, location, customer, customerInfo }) => ({
                url: `/appointments`,
                method: 'GET',
                params: {
                    mode,
                    date,
                    'filters[employee]': employee,
                    'filters[location]': location,
                    'filters[customer]': customer,
                    'filters[customerInfo]': customerInfo
                }
            }),
            providesTags: () => ['Appointment'],
            keepUnusedDataFor: 30
        }),
        fetchAllPaginatedAppointments: build.query<IPaginateResponse<ICalendarAppointment[]>, AppointmentPaginatedFetchProps>({
            query: ({ employee, location, customer, customerInfo, page = 1, per_page = 5 }) => ({
                url: `/appointments`,
                method: 'GET',
                params: {
                    'filters[employee]': employee,
                    'filters[location]': location,
                    'filters[customer]': customer,
                    'filters[customerInfo]': customerInfo,
                    page,
                    per_page,
                    sort: 'start_at',
                    order: 'desc'
                }
            }),
            providesTags: () => ['Appointment'],
            keepUnusedDataFor: 30
        }),

        fetchGlobalAppointments: build.query<IGlobalAppointment[], AppointmentsFetchProps>({
            query: ({ mode, date, employee, location, customer, customerInfo }) => ({
                url: `/global-appointments`,
                method: 'GET',
                params: {
                    mode,
                    date,
                    'filters[employee]': employee,
                    'filters[location]': location,
                    'filters[customer]': customer,
                    'filters[customerInfo]': customerInfo
                }
            }),
            providesTags: () => ['Appointment'],
            keepUnusedDataFor: 30
        }),
        getAppointment: build.query<IAppointment, number>({
            query: (id) => ({
                url: `/appointments/${id}`,
                method: 'GET'
            }),
            providesTags: () => ['Appointment']
        }),
        createAppointment: build.mutation<IAppointment, IAppointmentPayload>({
            query: (data) => ({
                url: `/appointments/`,
                method: 'POST',
                data
            }),
            invalidatesTags: ['Appointment', 'WorkloadData']
        }),
        updateAppointment: build.mutation<IAppointment, { appointmentId: string; data: IAppointmentPayload }>({
            query: ({ appointmentId, data }) => ({
                url: `/appointments/${appointmentId}`,
                method: 'PUT',
                data
            }),
            invalidatesTags: ['Appointment', 'WorkloadData']
        }),

        createAppointmentPayment: build.mutation<unknown, { appointmentId: string; payments: IPayment[] }>({
            query: ({ appointmentId, payments }) => ({
                url: `/appointments/${appointmentId}/payments`,
                method: 'POST',
                data: { payments }
            }),
            invalidatesTags: ['Appointment']
        }),

        updateAppointmentPayment: build.mutation<
            IAppointment,
            {
                appointmentId: string;
                data: IPayment;
                paymentIndex: number;
            }
        >({
            query: ({ appointmentId, data, paymentIndex }) => ({
                url: `/appointments/${appointmentId}/payments/${paymentIndex}`,
                method: 'PUT',
                data
            }),
            invalidatesTags: ['Appointment']
        }),

        deleteAppointmentPayment: build.mutation<unknown, { appointmentId: string; paymentIndex: number }>({
            query: ({ appointmentId, paymentIndex }) => ({
                url: `/appointments/${appointmentId}/payments/${paymentIndex}`,
                method: 'DELETE'
            }),
            invalidatesTags: ['Appointment']
        }),

        deleteAppointment: build.mutation<IAppointment, string | number>({
            query: (appointmentId) => ({
                url: `/appointments/${appointmentId}`,
                method: 'DELETE'
            }),
            invalidatesTags: ['Appointment', 'WorkloadData']
        }),
        setAppointmentStatus: build.mutation<
            IAppointment,
            {
                appointmentId: string | number;
                data: AppointmentStatusPayload;
            }
        >({
            query: ({ appointmentId, data }) => ({
                url: `/appointments/${appointmentId}/status`,
                method: 'PUT',
                data
            }),
            invalidatesTags: ['Appointment']
        }),
        getTimeSlots: build.query<TimeSlot[], TimeSlotsParams>({
            query: ({ date, service, location, employee }) => ({
                url: `/appointments/slots/`,
                method: 'GET',
                params: {
                    date,
                    service,
                    location,
                    employee
                }
            }),
            providesTags: () => ['Appointment']
        }),
        getAppointmentHistory: build.query<IAppointmentHistory[], number>({
            query: (id) => ({
                url: `/appointments/${id}/audits`,
                method: 'GET'
            }),
            providesTags: () => ['Appointment']
        }),
        sendInviteToSchedule: build.mutation<unknown, IAppointmentInvitePayload>({
            query: (data) => ({
                url: `/appointment-invites`,
                method: 'POST',
                data
            })
        }),

        sendSelfCheckoutInvite: build.mutation<unknown, { phone: string; appointment_id: number }>({
            query: ({ phone, appointment_id }) => ({
                url: `/appointments/${appointment_id}/self-checkout-invite`,
                method: 'POST',
                data: { phone }
            })
        }),

        resendWaiverConfirmation: build.mutation<unknown, number>({
            query: (id) => ({
                url: `appointments/${id}/waiver-reminder`,
                method: 'POST'
            }),
            invalidatesTags: ['Appointment']
        }),

        listAppointmentProducts: build.query<IAppointmentProduct[], { appointmentId: number }>({
            query: ({ appointmentId }) => ({
                url: `appointments/${appointmentId}/products`,
                method: 'GET'
            }),
            providesTags: ['AppointmentProducts']
        }),

        showAppointmentProduct: build.query<IAppointmentProduct, { appointmentId: number; productId: number }>({
            query: ({ appointmentId, productId }) => ({
                url: `appointments/${appointmentId}/products/${productId}`,
                method: 'GET'
            }),
            providesTags: ['AppointmentProducts']
        }),

        createAppointmentProduct: build.mutation<
            IAppointmentProduct,
            {
                appointmentId: number;
                productId: number;
                payload: { price: number; amount: number };
            }
        >({
            query: ({ appointmentId, productId, payload }) => ({
                url: `appointments/${appointmentId}/products/${productId}`,
                method: 'POST',
                data: payload
            }),
            invalidatesTags: ['Appointment']
        }),

        updateAppointmentProduct: build.mutation<
            IAppointmentProduct,
            {
                appointmentId: number;
                productId: number;
                payload: { price: number; amount: number };
            }
        >({
            query: ({ appointmentId, productId, payload }) => ({
                url: `appointments/${appointmentId}/products/${productId}`,
                method: 'PUT',
                data: payload
            }),
            invalidatesTags: ['Appointment']
        }),

        deleteAppointmentProduct: build.mutation<
            unknown,
            {
                appointmentId: number;
                productId: number;
            }
        >({
            query: ({ appointmentId, productId }) => ({
                url: `appointments/${appointmentId}/products/${productId}`,
                method: 'DELETE'
            }),
            invalidatesTags: ['Appointment']
        }),

        resendDepositRequest: build.mutation<unknown, string | number>({
            query: (appointmentId) => ({
                url: `appointments/${appointmentId}/resend-deposit-request`,
                method: 'POST'
            })
        }),

        publicConfirmAppointment: build.mutation<
            Pick<IAppointment, 'uuid' | 'status' | 'services' | 'employee' | 'start_at' | 'location'>,
            {
                company_slug: string;
                appointment_uuid: string;
            }
        >({
            query: ({ company_slug, appointment_uuid }) => ({
                url: `public/company/${company_slug}/appointment/${appointment_uuid}/confirm`,
                method: 'POST'
            })
        }),

        removeWaiver: build.mutation<unknown, { appointmentId: string | number; serviceId: string | number }>({
            query: ({ appointmentId, serviceId }) => ({
                url: `appointments/${appointmentId}/waiver-reset/${serviceId}`,
                method: 'POST'
            }),
            invalidatesTags: ['Appointment']
        }),

        // Recurring Events
        getRecurringEvents: build.query<IRecurringEvent[], AppointmentsFetchProps>({
            query: ({ employee, location, customer, customerInfo, ...rest }) => ({
                url: `recurring-events`,
                method: 'GET',
                params: {
                    'filters[employee]': employee,
                    'filters[location]': location,
                    'filters[customer]': customer,
                    'filters[customerInfo]': customerInfo,
                    ...rest
                }
            }),
            providesTags: () => ['RecurringEvents'],
            keepUnusedDataFor: 30
        }),

        getRecurringEventById: build.query<IDetailedRecurringEvent, { eventId: number | string; entryDate?: string }>({
            query: ({ eventId, entryDate }) => ({
                url: `recurring-events/${eventId}`,
                method: 'GET',
                params: {
                    entry_date: entryDate
                }
            }),
            providesTags: () => ['RecurringEvents'],
            keepUnusedDataFor: 30
        }),

        getRecurringEventHistory: build.query<IAppointmentHistory[], number | string>({
            query: (recurring_event_id) => ({
                url: `recurring-events/${recurring_event_id}/audits`,
                method: 'GET'
            }),
            providesTags: () => ['RecurringEvents'],
            keepUnusedDataFor: 30
        }),

        createRecurringEvent: build.mutation<IRecurringEvent, ICreateRecurringEventPayload>({
            query: (data) => ({
                url: 'recurring-events',
                method: 'POST',
                data
            }),
            invalidatesTags: ['RecurringEvents']
        }),

        updateRecurringEvent: build.mutation<IDetailedRecurringEvent, IUpdateRecurringEventPayload>({
            query: ({ recurring_event_id, ...data }) => ({
                url: `recurring-events/${recurring_event_id}`,
                method: 'PUT',
                data
            }),
            invalidatesTags: ['RecurringEvents']
        }),

        deleteRecurringEvent: build.mutation<unknown, number | string>({
            query: (recurring_event_id) => ({
                url: `recurring-events/${recurring_event_id}`,
                method: 'DELETE'
            }),
            invalidatesTags: ['RecurringEvents']
        }),

        getWorkloadData: build.query<WorkloadData, WorkloadArgs>({
            query: (params) => ({
                url: `appointments/workload`,
                method: 'GET',
                params
            }),
            providesTags: ['WorkloadData']
        })
    })
});

export default appointmentAPI;
